<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert, useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import BotMetrics from './components/BotMetrics.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ReportContainer from './ReportContainer.vue';
import ReportHeader from './components/ReportHeader.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';

const { t } = useI18n();
const store = useStore();

const from = ref(0);
const to = ref(0);
const groupBy = ref(GROUP_BY_FILTER[1]);
const businessHours = ref(false);

const requestPayload = () => ({
  from: from.value,
  to: to.value,
});

const reportKeys = {
  BOT_RESOLUTION_COUNT: 'bot_resolutions_count',
  BOT_HANDOFF_COUNT: 'bot_handoffs_count',
};

const getRequestPayload = () => ({
  from: from.value,
  to: to.value,
  groupBy: groupBy.value?.period,
  businessHours: businessHours.value,
});

const fetchBotSummary = () => {
  try {
    store.dispatch('fetchBotSummary', getRequestPayload());
  } catch {
    useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
  }
};

const fetchChartData = () => {
  Object.keys(reportKeys).forEach(async key => {
    try {
      await store.dispatch('fetchAccountReport', {
        metric: reportKeys[key],
        ...getRequestPayload(),
      });
    } catch {
      useAlert(t('REPORT.DATA_FETCHING_FAILED'));
    }
  });
};

const fetchAllData = () => {
  fetchBotSummary();
  fetchChartData();
};

const { startRefetching, stopRefetching } = useLiveRefresh(fetchAllData);

const onFilterChange = ({ from: f, to: t2, groupBy: g, businessHours: bh }) => {
  from.value = f;
  to.value = t2;
  groupBy.value = g;
  businessHours.value = bh;
  fetchAllData();
  stopRefetching();
  startRefetching();

  useTrack(REPORTS_EVENTS.FILTER_REPORT, {
    filterValue: { from: f, to: t2, groupBy: g, businessHours: bh },
    reportType: 'bots',
  });
};
</script>

<template>
  <ReportHeader :header-title="t('BOT_REPORTS.HEADER')" />
  <div class="flex flex-col gap-4">
    <ReportFilterSelector
      :show-agents-filter="false"
      show-group-by-filter
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <BotMetrics :filters="requestPayload()" />
    <ReportContainer
      account-summary-key="getBotSummary"
      summary-fetching-key="getBotSummaryFetchingStatus"
      :group-by="groupBy"
      :report-keys="reportKeys"
    />
  </div>
</template>
